import {
    Box,
    Typography,
    IconButton,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
} from '@mui/material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

function SortableExerciseCard({ exercise, onEdit, onDelete, isEditing, isDragging, onDeleteSet }) {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
        id: exercise.uebung_id
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <Accordion
            ref={setNodeRef}
            style={style}
            defaultExpanded
            sx={{
                background: 'linear-gradient(145deg, #1e293b 0%, #0f172a 100%)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                borderRadius: '16px !important',
                mb: 1.5,
                '&:before': { display: 'none' },
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: '#3b82f6' }} />}
                sx={{
                    background: 'rgba(59, 130, 246, 0.1)',
                    '&:hover': { background: 'rgba(59, 130, 246, 0.15)' },
                    py: 1.5,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                    {isEditing && (
                        <Box
                            {...attributes}
                            {...listeners}
                            size="small"
                            sx={{ cursor: 'grab', touchAction: 'none', '&:active': { cursor: 'grabbing' } }}
                        >
                            <DragIndicatorIcon sx={{ color: '#3b82f6' }} />
                        </Box>
                    )}
                    <Box sx={{ flex: 1 }}>
                        <Typography fontWeight={700} color="#e0f2fe">
                            {exercise.uebung_name}
                        </Typography>
                        <Typography variant="caption" color="#64748b">
                            {exercise.saetze.length} Satz{exercise.saetze.length !== 1 ? 'e' : ''}
                        </Typography>
                    </Box>
                    {isEditing && (
                        <Box
                            onClick={(e) => { e.stopPropagation(); onDelete(); }}
                            size="small"
                            sx={{ color: '#ef4444' }}
                        >
                            <DeleteIcon />
                        </Box>
                    )}
                </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 2, pb: 2 }}>
                {isEditing ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {exercise.saetze.map((satz, idx) => (
                            <Box key={`${exercise.uebung_id}-satz-${idx}`} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <Typography sx={{ color: '#93c5fd', minWidth: 60 }}>
                                    Satz {idx + 1}:
                                </Typography>
                                <TextField
                                    label="Wdh"
                                    type="number"
                                    size="small"
                                    value={satz.wiederholungen ?? ''} // Zeige null als ''
                                    onChange={(e) => onEdit(exercise.uebung_id, idx, 'wiederholungen', e.target.value)}
                                    sx={{ width: 80 }}
                                    inputProps={{ min: 0 }}
                                />
                                <TextField
                                    label="Gewicht (kg)"
                                    type="number"
                                    size="small"
                                    value={satz.gewicht_kg ?? ''} // Zeige null als ''
                                    onChange={(e) => onEdit(exercise.uebung_id, idx, 'gewicht_kg', e.target.value)}
                                    sx={{ width: 100 }}
                                    inputProps={{ step: "0.5", min: 0 }}
                                />
                                <IconButton
                                    onClick={() => onDeleteSet(exercise.uebung_id, idx)}
                                    size="small"
                                    sx={{ color: '#ef4444' }}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ background: 'rgba(59, 130, 246, 0.1)' }}>
                                    <TableCell sx={{ color: '#93c5fd', fontWeight: 700, fontSize: '0.75rem' }}>Satz</TableCell>
                                    <TableCell sx={{ color: '#93c5fd', fontWeight: 700, fontSize: '0.75rem' }}>Wdh.</TableCell>
                                    <TableCell sx={{ color: '#93c5fd', fontWeight: 700, fontSize: '0.75rem' }}>Gewicht</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {exercise.saetze.map((satz, idx) => (
                                    <TableRow key={idx}>
                                        <TableCell sx={{ color: '#e0f2fe', fontWeight: 600, py: 1 }}>{idx + 1}</TableCell>
                                        <TableCell sx={{ color: '#e0f2fe', fontWeight: 600, py: 1 }}>{satz.wiederholungen}</TableCell>
                                        <TableCell sx={{ color: '#e0f2fe', fontWeight: 600, py: 1 }}>
                                            {satz.gewicht_kg ? `${satz.gewicht_kg} kg` : '–'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </AccordionDetails>
        </Accordion>
    )
}

export default SortableExerciseCard;
